import { z } from 'zod';
import { addDays } from './facilities-preventive.ts';
import { fromScaled, toScaled } from './money.ts';

/**
 * Divergências da conferência da conta (ADR 0084, §15 da especificação). Função pura: recebe o
 * que a conta tem (linhas do banco, baixas registradas, saldos do dia, linhas de outras contas da
 * empresa) e devolve o que alguém precisa olhar. Cada divergência tem uma chave estável
 * (`refKey`): a mesma situação, na conferência seguinte, é a mesma divergência.
 */

export const CASH_DIVERGENCE_KINDS = [
  'balance_mismatch',
  'book_without_bank',
  'bank_without_book',
  'duplicate_receipt',
  'duplicate_payment',
  'amount_mismatch',
  'duplicate_forecast',
  'wrong_account',
  'currency_mismatch',
  'date_mismatch',
] as const;
export type CashDivergenceKind = (typeof CASH_DIVERGENCE_KINDS)[number];

export const CASH_DIVERGENCE_STATUSES = ['pending', 'in_review', 'resolved', 'dismissed'] as const;
export type CashDivergenceStatus = (typeof CASH_DIVERGENCE_STATUSES)[number];

/** Dias entre a baixa e a linha do banco para ainda serem o mesmo pagamento. */
export const DIVERGENCE_WINDOW = 3;
/** Valor divergente: até 5% de diferença entre a linha e a baixa (tarifa, juros, câmbio). */
export const AMOUNT_TOLERANCE_PCT = 5n;

export interface DivergenceCandidate {
  kind: CashDivergenceKind;
  refKey: string;
  refDate: string | null;
  /** Com sinal: o valor apontado ou a diferença. */
  amount: string | null;
  details: Record<string, unknown>;
}

export interface DivergenceBankLine {
  id: string;
  date: string;
  /** Com sinal (entrada positiva). */
  amount: string;
  description: string;
  /** Lote de extrato de onde veio (digitação manual também é um lote). */
  importId: string | null;
}

export interface DivergenceBookItem {
  type: string;
  id: string;
  date: string;
  amount: string;
  label: string;
  currency?: string;
}

export interface DivergenceInput {
  today: string;
  accountCurrency: string;
  /** Períodos com extrato gravado: neles, a baixa sem linha é divergência. */
  covered: readonly { start: string; end: string }[];
  /** Linhas do extrato no período, todas (para repetição). */
  bankLines: readonly DivergenceBankLine[];
  /** Ids das linhas ainda sem conciliação. */
  openBankIds: ReadonlySet<string>;
  /** Baixas ainda sem conciliação. */
  openBook: readonly DivergenceBookItem[];
  /** Saldos do dia com informado e calculado. */
  days: readonly { date: string; reported: string | null; calculated: string | null }[];
  /** Linhas em aberto de outras contas da empresa na mesma moeda. */
  otherAccounts: readonly {
    accountKey: string;
    nickname: string;
    openLines: readonly DivergenceBankLine[];
  }[];
  /** Conciliações vigentes: data prevista (vencimento ou programação) e data do banco. */
  reconciled: readonly {
    bookType: string;
    bookId: string;
    label: string;
    expectedDate: string | null;
    bankDate: string;
    amount: string;
  }[];
}

const abs = (v: bigint) => (v < 0n ? -v : v);
const days = (a: string, b: string) =>
  Math.round((Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / 86_400_000);
const near = (a: string, b: string) => Math.abs(days(a, b)) <= DIVERGENCE_WINDOW;
const words = (v: string) =>
  v.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().replace(/\s+/g, ' ').trim();

export function detectDivergences(input: DivergenceInput): DivergenceCandidate[] {
  const out: DivergenceCandidate[] = [];
  const openBank = input.bankLines.filter((b) => input.openBankIds.has(b.id));
  const isCovered = (d: string) => input.covered.some((c) => c.start <= d && d <= c.end);
  const explainedBank = new Set<string>();
  const explainedBook = new Set<string>();
  const bookKey = (k: DivergenceBookItem) => `${k.type}:${k.id}`;

  // Saldo do banco diferente do calculado.
  for (const d of input.days) {
    if (d.reported === null || d.calculated === null) continue;
    const diff = toScaled(d.reported) - toScaled(d.calculated);
    if (diff === 0n) continue;
    out.push({
      kind: 'balance_mismatch',
      refKey: `balance:${d.date}`,
      refDate: d.date,
      amount: fromScaled(diff),
      details: { reported: d.reported, calculated: d.calculated },
    });
  }

  // Moeda divergente: baixa em moeda diferente da conta.
  for (const k of input.openBook) {
    if (!k.currency || k.currency === input.accountCurrency) continue;
    explainedBook.add(bookKey(k));
    out.push({
      kind: 'currency_mismatch',
      refKey: `currency:${bookKey(k)}`,
      refDate: k.date,
      amount: k.amount,
      details: { label: k.label, currency: k.currency, accountCurrency: input.accountCurrency },
    });
  }

  // Valor divergente: uma linha e uma baixa próximas, mesmo sentido, diferença pequena e só um
  // par possível (empate nunca é escolhido).
  const pairs = new Map<string, DivergenceBookItem[]>();
  const byBook = new Map<string, string[]>();
  for (const b of openBank) {
    const bv = toScaled(b.amount);
    for (const k of input.openBook) {
      if (explainedBook.has(bookKey(k)) || !near(b.date, k.date)) continue;
      const kv = toScaled(k.amount);
      if (kv === bv || bv < 0n !== kv < 0n) continue;
      const big = abs(bv) > abs(kv) ? abs(bv) : abs(kv);
      if (abs(bv - kv) * 100n > big * AMOUNT_TOLERANCE_PCT) continue;
      pairs.set(b.id, [...(pairs.get(b.id) ?? []), k]);
      byBook.set(bookKey(k), [...(byBook.get(bookKey(k)) ?? []), b.id]);
    }
  }
  for (const b of openBank) {
    const ks = pairs.get(b.id);
    if (!ks || ks.length !== 1) continue;
    const k = ks[0]!;
    if (byBook.get(bookKey(k))?.length !== 1) continue;
    explainedBank.add(b.id);
    explainedBook.add(bookKey(k));
    out.push({
      kind: 'amount_mismatch',
      refKey: `amount:${b.id}:${bookKey(k)}`,
      refDate: b.date,
      amount: fromScaled(toScaled(b.amount) - toScaled(k.amount)),
      details: {
        movementId: b.id,
        bankAmount: b.amount,
        bookAmount: k.amount,
        label: k.label,
        description: b.description,
      },
    });
  }

  // Conta errada: a baixa não tem linha nesta conta, mas outra conta da empresa tem uma linha em
  // aberto com o mesmo valor e data próxima (só quando há uma só).
  for (const k of input.openBook) {
    if (explainedBook.has(bookKey(k))) continue;
    if (openBank.some((b) => toScaled(b.amount) === toScaled(k.amount) && near(b.date, k.date))) {
      continue;
    }
    const found = input.otherAccounts.flatMap((a) =>
      a.openLines
        .filter((l) => toScaled(l.amount) === toScaled(k.amount) && near(l.date, k.date))
        .map((l) => ({ account: a, line: l })),
    );
    if (found.length !== 1) continue;
    const { account, line } = found[0]!;
    explainedBook.add(bookKey(k));
    out.push({
      kind: 'wrong_account',
      refKey: `account:${bookKey(k)}:${line.id}`,
      refDate: k.date,
      amount: k.amount,
      details: {
        label: k.label,
        otherAccountKey: account.accountKey,
        otherAccountNickname: account.nickname,
        otherMovementId: line.id,
        otherDate: line.date,
      },
    });
  }

  // Repetição: mesma data, valor e histórico vindos de lotes diferentes (digitação e arquivo,
  // ou dois arquivos). Repetição no mesmo arquivo é do banco (duas tarifas iguais) e não conta.
  const groups = new Map<string, DivergenceBankLine[]>();
  for (const b of input.bankLines) {
    const k = `${b.date}|${toScaled(b.amount)}|${words(b.description)}`;
    groups.set(k, [...(groups.get(k) ?? []), b]);
  }
  for (const g of groups.values()) {
    if (g.length < 2 || new Set(g.map((b) => b.importId ?? '')).size < 2) continue;
    const ids = g.map((b) => b.id).sort();
    const inflow = !g[0]!.amount.startsWith('-');
    for (const id of ids) explainedBank.add(id);
    out.push({
      kind: inflow ? 'duplicate_receipt' : 'duplicate_payment',
      refKey: `dup:${ids[0]}:${ids[1]}`,
      refDate: g[0]!.date,
      amount: g[0]!.amount,
      details: { movementIds: ids, description: g[0]!.description, count: g.length },
    });
  }

  // Linha do banco sem baixa (até ontem) e baixa sem linha num dia com extrato.
  for (const b of openBank) {
    if (explainedBank.has(b.id) || b.date >= input.today) continue;
    out.push({
      kind: 'bank_without_book',
      refKey: `bank:${b.id}`,
      refDate: b.date,
      amount: b.amount,
      details: { movementId: b.id, description: b.description },
    });
  }
  for (const k of input.openBook) {
    if (explainedBook.has(bookKey(k)) || k.date >= input.today || !isCovered(k.date)) continue;
    // Ainda pode chegar no banco dentro da janela.
    if (addDays(k.date, DIVERGENCE_WINDOW) >= input.today) continue;
    out.push({
      kind: 'book_without_bank',
      refKey: `book:${bookKey(k)}`,
      refDate: k.date,
      amount: k.amount,
      details: { label: k.label, type: k.type, id: k.id },
    });
  }

  // Data prevista diferente da efetiva (conciliadas).
  for (const r of input.reconciled) {
    if (!r.expectedDate || r.expectedDate === r.bankDate) continue;
    out.push({
      kind: 'date_mismatch',
      refKey: `date:${r.bookType}:${r.bookId}`,
      refDate: r.bankDate,
      amount: r.amount,
      details: {
        label: r.label,
        expectedDate: r.expectedDate,
        effectiveDate: r.bankDate,
        days: days(r.bankDate, r.expectedDate),
      },
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Entradas da API
// ---------------------------------------------------------------------------

export const divergenceReviewInput = z.object({
  note: z.string().trim().min(1).max(2000).nullable().optional(),
});
export const divergenceCloseInput = z.object({
  reason: z.string().trim().min(3).max(1000),
});
